import { Sparkles, Heart, Star, Award, Users } from "lucide-react";
import { Link } from "react-router-dom";
import { waGeneralLink } from "@/lib/whatsapp";
import { useState, useEffect } from "react";
import { supabase, getStorageUrl } from "@/lib/supabase";

export default function About() {
  const [aboutImg, setAboutImg] = useState("https://images.unsplash.com/photo-1550614000-4b95dd2475e2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200");

  useEffect(() => {
    supabase.storage.from("images").list("about_posters", {
      limit: 1,
      sortBy: { column: "created_at", order: "desc" }
    }).then(({ data, error }) => {
      if (!error && data && data.length > 0 && data[0].name !== ".emptyFolderPlaceholder") {
        setAboutImg(getStorageUrl(`about_posters/${data[0].name}`));
      }
    });
  }, []);

  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section className="border-b-[3px] border-black bg-[var(--accent)] text-white candy-dots">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-14 md:py-20">
          <div className="nb-pill inline-block mb-4 !text-black">OUR STORY</div>
          <h1 className="font-display font-black uppercase text-5xl md:text-7xl tracking-tight">
            Built in Buttaipet,<br/> dressed for the world.
          </h1>
        </div>
      </section>

      {/* Story */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-16 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <div className="relative">
          <div className="aspect-[4/5] rounded-[36px] nb-border overflow-hidden nb-shadow-lg">
            <img src={aboutImg} alt="Khasim Trendz" className="w-full h-full object-cover" />
          </div>
          <div className="sticker w-32 h-32 -top-6 -right-6 bg-[var(--secondary)] wiggle">
            <div className="text-center text-sm leading-tight">
              <div className="text-2xl font-black">100%</div>
              <div>LOCAL LOVE</div>
            </div>
          </div>
        </div>
        <div>
          <h2 className="font-display font-black uppercase text-4xl md:text-5xl mb-5">A candy-bright closet for every guy.</h2>
          <p className="text-lg text-black/80 mb-4">
            Khasim Trendz began as a tiny corner shop in Buttaipet, Machilipatnam — a one-man passion project to bring trend-driven menswear to the streets we grew up on.
          </p>
          <p className="text-lg text-black/80 mb-4">
            Today, we curate everything from oversized streetwear tees and acid-washed jeans to crisp formal shirts, premium watches, sneakers, sunglasses, caps and belts — handpicked for guys who refuse to look generic.
          </p>
          <p className="text-lg text-black/80">
            Every fit on our floor is selected for quality, value, and that little bit of sweetness that makes you stand out.
          </p>
        </div>
      </section>

      {/* Values */}
      <section className="bg-[var(--secondary)] border-y-[3px] border-black">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-16">
          <h2 className="font-display font-black uppercase text-4xl md:text-5xl mb-10 text-center">What we stand for</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { icon: <Sparkles strokeWidth={2.5} size={32} />, title: "Fresh Always", text: "New drops every week. We never sit on stock." },
              { icon: <Heart strokeWidth={2.5} size={32} />, title: "Local First", text: "Built by Machilipatnam, for Machilipatnam." },
              { icon: <Award strokeWidth={2.5} size={32} />, title: "Premium Picks", text: "Watches, shoes & accessories you'll actually flex." },
              { icon: <Users strokeWidth={2.5} size={32} />, title: "Human Service", text: "WhatsApp us — talk to a real person, not a bot." },
            ].map((v, i) => (
              <div key={i} className="nb-card p-6 bg-white" data-testid={`value-${i}`}>
                <div className="w-14 h-14 rounded-full bg-[var(--primary)] text-white border-[3px] border-black flex items-center justify-center mb-4 shadow-[3px_3px_0_0_#0A0A0A]">
                  {v.icon}
                </div>
                <h3 className="font-display font-black text-xl uppercase mb-2">{v.title}</h3>
                <p className="text-black/80">{v.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-16 text-center">
        <Star size={40} strokeWidth={3} fill="#FF3366" color="#FF3366" className="mx-auto mb-4" />
        <h2 className="font-display font-black uppercase text-4xl md:text-6xl mb-4">Come hang at our store.</h2>
        <p className="text-lg max-w-2xl mx-auto mb-8">Walk in to Buttaipet for the full experience, or DM us on WhatsApp — we'll send pics, sizes & prices instantly.</p>
        <div className="flex justify-center gap-3 flex-wrap">
          <a href={waGeneralLink()} target="_blank" rel="noreferrer" className="nb-btn bg-[var(--whatsapp)] text-white" data-testid="about-wa-btn">Chat on WhatsApp</a>
          <Link to="/contact" className="nb-btn bg-white text-black">Find the store</Link>
        </div>
      </section>
    </div>
  );
}
